import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Pagination,
  Tabs,
  Tab,
  Divider,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PersonOutline, FolderOutlined, AttachMoneyOutlined, GroupOutlined, LocationOnOutlined, ArchiveOutlined, Apartment } from '@mui/icons-material';
import { useAuth } from '../context/authContext';
import { useAppSelector, useAppDispatch } from '../redux/redux.hook';
import { purgeTimesheets } from '../redux/timeSheet.slice';
import { fetchData } from '../redux/data.slice';
import {
  WorkManager,
  Employee,
  SalaryCode,
  Data,
} from '../interfaces/timesheet';

interface PaginationState {
  workManagersPage: number;
  foremansPage: number;
  salariesPage: number;
  employeesPage: number;
  sitesPage: number;
}

export const Dashboard: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const dispatch = useAppDispatch();
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Redux state
  const { lastSynced } = useAppSelector(state => state.sync);
  const { pendingItems } = useAppSelector(state => state.timesheets);
  const { data, loading, error } = useAppSelector(state => state.data);

  const [pagination, setPagination] = useState<PaginationState>({
    workManagersPage: 1,
    foremansPage: 1,
    salariesPage: 1,
    employeesPage: 1,
    sitesPage: 1,
  });

  const [activeTab, setActiveTab] = useState(0); // Active tab state
  const itemsPerPage = 5;

  const fetch = async () => {
    if (isAuthenticated) {
      await dispatch(fetchData()).unwrap();
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const handleArchiveClick = () => {
    setArchiveDialogOpen(true);
  };

  const handleCloseArchiveDialog = () => {
    setArchiveDialogOpen(false);
  };

  const handleArchiveConfirm = async () => {
    try {
      setArchiving(true);
      await dispatch(purgeTimesheets()).unwrap();
      setArchiving(false);
      setArchiveDialogOpen(false);
    } catch (error) {
      setArchiving(false);
    }
  };

  const formatSyncTime = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const handleChangePage = (category: keyof PaginationState, page: number) => {
    setPagination(prev => ({
      ...prev,
      [category]: page,
    }));
  };

  const getPaginatedData = <T extends keyof Data>(category: T): Data[T] => {
    const dataCategory = data?.[category] ?? [];
    const startIndex = (pagination[`${category}Page` as keyof PaginationState] - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return dataCategory.slice(startIndex, endIndex) as Data[T];
  };

  const pageCount = (category: keyof Data) => {
    const dataCategory = data?.[category] ?? [];
    return Math.ceil(dataCategory.length / itemsPerPage);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Type Guards
  const hasMatricule = (item: any): item is Employee | WorkManager => {
    return 'matricule' in item;
  };

  const hasRole = (item: any): item is Employee => {
    return 'role' in item;
  };

  const hasInterim = (item: any): item is Employee => {
    return 'isInterim' in item;
  };

  const hasInterimCompanyName = (item: any): item is Employee => {
    return 'interimCompanyName' in item;
  };

  const hasCode = (item: any): item is SalaryCode => {
    return 'code' in item;
  };

  const hasEmail = (item: any): item is WorkManager => {
    return 'email' in item;
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Detect mobile screen

  const renderCard = (category: keyof Data, icon: React.ReactNode, title: string) => {
    const paginatedData = getPaginatedData(category);

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon} {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {paginatedData.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6">{item.name}</Typography>
                  {hasRole(item) && <Typography>Metier: {item.role}</Typography>}
                  {hasMatricule(item) && <Typography>Matricule: {item.matricule}</Typography>}
                  {hasCode(item) && <Typography>Code Salaire: {item.code}</Typography>}
                  {hasEmail(item) && <Typography>Email: {item.email}</Typography>}
                  {hasInterim(item) && item.isInterim &&
                    <Typography sx={{ mt: 2 }}><Chip label="intérimaire" color='warning' /> Chez
                      {hasInterimCompanyName(item) && item.interimCompanyName &&
                        <Chip sx={{ ml: 1 }} label={item.interimCompanyName} color='default' icon={<Apartment fontSize="small" />} />}
                    </Typography>
                  }

                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Pagination
          count={pageCount(category)}
          page={pagination[`${category}Page` as keyof PaginationState]}
          onChange={(e, page) => handleChangePage(`${category}Page` as keyof PaginationState, page)}
          color="primary"
          sx={{ mt: 2 }}
        />
      </Box>
    );
  };

  const renderTable = (category: keyof Data, icon: React.ReactNode, title: string) => {
    const paginatedData = getPaginatedData(category);

    return (
      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon} {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer component={Paper} sx={{ maxHeight: '400px', overflowY: 'auto' }}>
          <Table sx={{ minWidth: 650 }} aria-label={title}>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                {paginatedData.some(hasRole) && <TableCell>Metier</TableCell>}
                {paginatedData.some(hasMatricule) && <TableCell>Matricule</TableCell>}
                {paginatedData.some(hasCode) && <TableCell>Code Salaire</TableCell>}
                {paginatedData.some(hasEmail) && <TableCell>Email</TableCell>}
                {paginatedData.some(hasInterim) && <TableCell>Interimaire</TableCell>}
                {paginatedData.some(hasInterimCompanyName) && <TableCell>Société</TableCell>}

              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((item, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row">{item.name}</TableCell>
                  {hasRole(item) && <TableCell>{item.role}</TableCell>}
                  {hasMatricule(item) && <TableCell>{item.matricule}</TableCell>}
                  {hasCode(item) && <TableCell>{item.code}</TableCell>}
                  {hasEmail(item) && <TableCell>{item.email}</TableCell>}
                  <TableCell>{hasInterim(item) && item.isInterim && <Chip label="Oui" color='warning' />}</TableCell>
                  <TableCell>{hasInterimCompanyName(item) && item.interimCompanyName && <Chip label={item.interimCompanyName} color='default' icon={<Apartment fontSize="small" />} />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={pageCount(category)}
          page={pagination[`${category}Page` as keyof PaginationState]}
          onChange={(e, page) => handleChangePage(`${category}Page` as keyof PaginationState, page)}
          color="primary"
          sx={{ mt: 2 }}
        />
      </Paper>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} style={{ padding: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Tableau de bord</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction="row" spacing={2} alignItems="center">
          {pendingItems.length > 0 && (
            <Chip label={`${pendingItems.length} éléments en suspend`} color="primary" />
          )}
          <Button
            variant="contained"
            color="error"
            startIcon={<ArchiveOutlined />}
            onClick={handleArchiveClick}
            disabled={archiving || !data}
          >
            {archiving ? 'Archivage en cours...' : 'Archiver les données'}
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="body1">
          Bienvenu. <br />Ci-dessous, tous les éléments obtenus depuis les sources de données sur OneDrive.
        </Typography>
        {lastSynced && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Dernière synchronisation: {formatSyncTime(lastSynced)}
          </Typography>
        )}
      </Paper>

      {/* For mobile, replace Tabs with stacked cards */}
      {isMobile ? (
        <>
          {data && renderCard("workManagers", <PersonOutline color="primary" />, "Chefs d'équipes")}
          {data && renderCard("foremans", <FolderOutlined color="secondary" />, "Conducteurs de travaux")}
          {data && renderCard("salaries", <AttachMoneyOutlined color="error" />, "Codes salaires")}
          {data && renderCard("employees", <GroupOutlined color="success" />, "Employés")}
          {data && renderCard("sites", <LocationOnOutlined color="warning" />, "Chantiers")}
        </>
      ) : (
        <>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" centered>
            <Tab label="Chefs d'équipes" icon={<PersonOutline />} />
            <Tab label="Conducteurs de travaux" icon={<FolderOutlined />} />
            <Tab label="Codes salaires" icon={<AttachMoneyOutlined />} />
            <Tab label="Employés" icon={<GroupOutlined />} />
            <Tab label="Chantiers" icon={<LocationOnOutlined />} />
          </Tabs>

          {/* Render Tables based on selected tab */}
          {data && activeTab === 0 && renderTable("workManagers", <PersonOutline color="primary" />, "Chefs d'équipes")}
          {data && activeTab === 1 && renderTable("foremans", <FolderOutlined color="secondary" />, "Conducteurs de travaux")}
          {data && activeTab === 2 && renderTable("salaries", <AttachMoneyOutlined color="error" />, "Codes salaires")}
          {data && activeTab === 3 && renderTable("employees", <GroupOutlined color="success" />, "Employés")}
          {data && activeTab === 4 && renderTable("sites", <LocationOnOutlined color="warning" />, "Chantiers")}
        </>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      )}

      <Dialog open={archiveDialogOpen} onClose={handleCloseArchiveDialog}>
        <DialogTitle>Archiver les données</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr? La suppression des données est irréversible!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseArchiveDialog} disabled={archiving}>
            Annuler
          </Button>
          <Button
            onClick={handleArchiveConfirm}
            color="secondary"
            disabled={archiving}
            startIcon={archiving ? <CircularProgress size={20} /> : <ArchiveOutlined />}
          >
            {archiving ? 'Archivage en cours...' : 'Archiver'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
